/**
 * Ticket Hooks
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import * as ticketService from '../api/services/ticket.service';
import type {
  TicketDetail,
  TicketCreateRequest,
  TicketUpdateRequest,
  TicketSystem,
  TicketFunction,
  TicketMessage,
} from '../types';
import { toast } from '../stores/toast.store';

const KEYS = {
  systems: ['ticket', 'systems'] as const,
  functions: (systemId?: number) => ['ticket', 'functions', systemId] as const,
  tickets: ['ticket', 'list'] as const,
  ticket: (id: number) => ['ticket', 'detail', id] as const,
  messages: (id: number) => ['ticket', 'messages', id] as const,
};

/**
 * Hook for fetching ticket systems
 */
export function useTicketSystems() {
  return useQuery<TicketSystem[]>({
    queryKey: KEYS.systems,
    queryFn: async () => {
      const result = await ticketService.getTicketSystems();
      if (!result.success) {
        throw new Error(result.error?.message || 'Failed to fetch systems');
      }
      return result.data!;
    },
    staleTime: 5 * 60 * 1000,
  });
}

/**
 * Hook for fetching ticket functions
 */
export function useTicketFunctions(systemId?: number) {
  return useQuery<TicketFunction[]>({
    queryKey: KEYS.functions(systemId),
    queryFn: async () => {
      const result = await ticketService.getTicketFunctions(systemId);
      if (!result.success) {
        throw new Error(result.error?.message || 'Failed to fetch functions');
      }
      return result.data!;
    },
    staleTime: 5 * 60 * 1000,
  });
}

interface UseTicketsParams {
  offset?: number;
  limit?: number;
  type?: string;
  state?: string;
  search?: string;
  showAll?: boolean;
}

/**
 * Hook for fetching ticket list
 */
export function useTickets(params: UseTicketsParams = {}) {
  return useQuery({
    queryKey: [...KEYS.tickets, params],
    queryFn: async () => {
      const result = await ticketService.getTicketList({
        offset: params.offset,
        limit: params.limit,
        type: params.type,
        state: params.state,
        search: params.search,
        show_all: params.showAll,
      });
      if (!result.success) {
        throw new Error(result.error?.message || 'Failed to fetch tickets');
      }
      return result.data!;
    },
  });
}

/**
 * Hook for fetching single ticket detail
 */
export function useTicketDetail(id: number | undefined) {
  return useQuery<TicketDetail>({
    queryKey: KEYS.ticket(id!),
    queryFn: async () => {
      const result = await ticketService.getTicket(id!);
      if (!result.success) {
        throw new Error(result.error?.message || 'Failed to fetch ticket');
      }
      return result.data!;
    },
    enabled: !!id,
  });
}

/**
 * Hook for fetching ticket messages
 */
export function useTicketMessages(id: number | undefined) {
  return useQuery<TicketMessage[]>({
    queryKey: KEYS.messages(id!),
    queryFn: async () => {
      const result = await ticketService.getTicketMessages(id!);
      if (!result.success) {
        throw new Error(result.error?.message || 'Failed to fetch messages');
      }
      return result.data!;
    },
    enabled: !!id,
  });
}

/**
 * Hook for creating a ticket
 */
export function useCreateTicket() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (data: TicketCreateRequest) => {
      const result = await ticketService.createTicket(data);
      if (!result.success) {
        throw new Error(result.error?.message || 'Failed to create ticket');
      }
      return result.data!;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: KEYS.tickets });
      toast.success('工單建立成功');
    },
    onError: (error: Error) => {
      toast.error('建立失敗', error.message);
    },
  });
}

/**
 * Hook for updating a ticket
 */
export function useUpdateTicket() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      id,
      data,
    }: {
      id: number;
      data: TicketUpdateRequest;
    }) => {
      const result = await ticketService.updateTicket(id, data);
      if (!result.success) {
        throw new Error(result.error?.message || 'Failed to update ticket');
      }
      return result.data!;
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: KEYS.tickets });
      queryClient.setQueryData(KEYS.ticket(data.id), data);
      toast.success('更新成功');
    },
    onError: (error: Error) => {
      toast.error('更新失敗', error.message);
    },
  });
}

/**
 * Hook for submitting a ticket
 */
export function useSubmitTicket() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (id: number) => {
      const result = await ticketService.submitTicket(id);
      if (!result.success) {
        throw new Error(result.error?.message || 'Failed to submit ticket');
      }
      return result.data!;
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: KEYS.tickets });
      queryClient.setQueryData(KEYS.ticket(data.id), data);
      toast.success('工單已送出');
    },
    onError: (error: Error) => {
      toast.error('送出失敗', error.message);
    },
  });
}

/**
 * Hook for user confirming test result
 */
export function useUserConfirmTicket() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ id, confirmed }: { id: number; confirmed: boolean }) => {
      const result = await ticketService.userConfirmTicket(id, confirmed);
      if (!result.success) {
        throw new Error(result.error?.message || 'Failed to confirm');
      }
      return result.data!;
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: KEYS.tickets });
      queryClient.setQueryData(KEYS.ticket(data.id), data);
    },
    onError: (error: Error) => {
      toast.error('操作失敗', error.message);
    },
  });
}

/**
 * Hook for deleting a ticket
 */
export function useDeleteTicket() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (id: number) => {
      const result = await ticketService.deleteTicket(id);
      if (!result.success) {
        throw new Error(result.error?.message || 'Failed to delete ticket');
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: KEYS.tickets });
      toast.success('工單已刪除');
    },
    onError: (error: Error) => {
      toast.error('刪除失敗', error.message);
    },
  });
}

/**
 * Hook for deleting attachment
 */
export function useDeleteTicketImage() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ ticketId, attachmentId }: { ticketId: number; attachmentId: number }) => {
      const result = await ticketService.deleteTicketImage(ticketId, attachmentId);
      if (!result.success) {
        throw new Error(result.error?.message || 'Failed to delete attachment');
      }
    },
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({ queryKey: KEYS.ticket(variables.ticketId) });
      toast.success('附件已刪除');
    },
    onError: (error: Error) => {
      toast.error('刪除失敗', error.message);
    },
  });
}

/**
 * Hook for uploading attachment
 */
export function useUploadTicketImage() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ ticketId, file }: { ticketId: number; file: File }) => {
      const result = await ticketService.uploadTicketImage(ticketId, file);
      if (!result.success) {
        throw new Error(result.error?.message || 'Failed to upload');
      }
      return result.data!;
    },
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({ queryKey: KEYS.ticket(variables.ticketId) });
      toast.success('上傳成功');
    },
    onError: (error: Error) => {
      toast.error('上傳失敗', error.message);
    },
  });
}

/**
 * Hook for adding a message
 */
export function useAddTicketMessage() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ ticketId, content }: { ticketId: number; content: string }) => {
      const result = await ticketService.createTicketMessage(ticketId, content);
      if (!result.success) {
        throw new Error(result.error?.message || 'Failed to send message');
      }
      return result.data!;
    },
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({ queryKey: KEYS.messages(variables.ticketId) });
      queryClient.invalidateQueries({ queryKey: KEYS.ticket(variables.ticketId) });
    },
    onError: (error: Error) => {
      toast.error('發送失敗', error.message);
    },
  });
}
